<template>
	<!-- Hero -->
	<section class="os-banner position-relative d-flex align-items-center justify-content-center">
		<div class="container-xl text-center os-banner-inner">
			<p class="os-eyebrow">吃荤搭素 · Chi Hun Da Su</p>
			<h1 class="text-white m-0">{{ c.heroTitle }}</h1>
			<p class="os-hero-sub">{{ c.heroSub }}</p>
		</div>
	</section>

	<!-- The name / philosophy -->
	<section class="os-section">
		<div class="container-xl">
			<div class="os-lead text-center">
				<p class="os-kicker">{{ c.nameKicker }}</p>
				<h2 class="os-h2">{{ c.nameTitle }}</h2>
				<p class="os-body">{{ c.nameBody1 }}</p>
				<p class="os-body">{{ c.nameBody2 }}</p>
			</div>
		</div>
	</section>

	<!-- Two formats -->
	<section class="os-section os-tint">
		<div class="container-xl">
			<h2 class="os-h2 text-center">{{ c.formatsTitle }}</h2>
			<p class="os-body text-center os-body-narrow">{{ c.formatsIntro }}</p>
			<div class="os-formats">
				<div class="os-format-card">
					<span class="os-format-tag">{{ c.setTag }}</span>
					<h3>{{ c.setTitle }}</h3>
					<p>{{ c.setBody }}</p>
				</div>
				<div class="os-format-card os-format-card--alt">
					<span class="os-format-tag">{{ c.dishTag }}</span>
					<h3>{{ c.dishTitle }}</h3>
					<p>{{ c.dishBody }}</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Freshness & macros promise -->
	<section class="os-promise">
		<div class="container-xl">
			<h2 class="os-h2 os-h2--light text-center">{{ c.promiseTitle }}</h2>
			<p class="os-body os-body-narrow text-center os-promise-sub">{{ c.promiseBody }}</p>
			<div class="os-proof-row">
				<div class="os-proof" v-for="(p, i) in c.proofs" :key="i">
					<span class="os-proof-ic">{{ p.ic }}</span>
					<span class="os-proof-txt">{{ p.txt }}</span>
				</div>
			</div>
		</div>
	</section>

	<!-- Founder story -->
	<section class="os-section">
		<div class="container-xl">
			<div class="os-founder">
				<div class="os-founder-media">
					<img src="/story-founder.webp" class="img-fluid" alt="A CHDS Power Bowl — braised beef, vegetables and rice" />
				</div>
				<div class="os-founder-copy">
					<p class="os-kicker">{{ c.founderKicker }}</p>
					<h2 class="os-h2 os-h2--left">{{ c.founderTitle }}</h2>
					<p class="os-body">{{ c.founderBody1 }}</p>
					<p class="os-body">{{ c.founderBody2 }}</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Why choose CHDS -->
	<section class="os-section os-tint">
		<div class="container-xl">
			<h2 class="os-h2 text-center">{{ c.whyTitle }}</h2>
			<div class="os-why-grid">
				<div class="os-why-card" v-for="(r, i) in c.reasons" :key="i">
					<span class="os-why-ic">{{ r.ic }}</span>
					<h3>{{ r.title }}</h3>
					<p>{{ r.body }}</p>
				</div>
			</div>
			<div class="os-cta text-center">
				<router-link to="/ordernow" class="os-btn os-btn--primary">{{ c.ctaMenu }}</router-link>
				<router-link to="/box" class="os-btn os-btn--ghost">{{ c.ctaBox }}</router-link>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed } from "vue";
import { useSEO } from "@/composables/useSEO";
import { useTranslationStore } from "@/stores/translation";

const tr = useTranslationStore();
const lang = computed(() => (tr.currentLanguage === "zh" ? "zh" : "en"));

const COPY = {
	en: {
		heroTitle: "Balance you can taste",
		heroSub: "Real Chinese cooking, engineered for a healthy life, delivered fresh across Melbourne.",
		nameKicker: "The name is the philosophy",
		nameTitle: "Balance is in our name",
		nameBody1:
			"CHDS stands for Chi Hun Da Su — 吃荤搭素 — “eat meat, paired with vegetables.” It's the oldest nutrition advice in Chinese culture: protein and plants, together, in balance, at every meal. We didn't invent balanced eating. We just cooked it fresh, put it in a container, and printed the macros on the side.",
		nameBody2:
			"We started as a small WeChat-community kitchen with fiercely loyal customers, and grew into a full bilingual delivery service — the same care, now for all of Melbourne.",
		formatsTitle: "Two ways to eat well",
		formatsIntro:
			"One kitchen, two doors in — whether you want dinner completely solved, or clean protein by the half-kilo to build meals your way.",
		setTag: "Meal Set",
		setTitle: "Dinner, completely solved",
		setBody:
			"A complete balanced meal in one container — around 150g of a protein dish, 150g of vegetables and 150g of rice. Zero decisions, zero cooking, ready in a couple of minutes. Add extra protein, rice or veg whenever you like.",
		dishTag: "Dish Only",
		dishTitle: "Clean protein, your way",
		dishBody:
			"300g or 600g of just the protein — chicken breast, braised beef and more, restaurant-cooked and clean. Better value per gram, built for athletes and high-protein eaters, and perfect for families plating dinner at home.",
		promiseTitle: "Fresh is non-negotiable",
		promiseBody:
			"We're a kitchen first and a delivery company second. Every meal is chef-cooked to order in our own restaurant kitchen — never frozen — and we publish the macros so you always know exactly what you're eating.",
		proofs: [
			{ ic: "🔥", txt: "Cooked to order, never frozen" },
			{ ic: "📊", txt: "Macros published on every dish" },
			{ ic: "🥦", txt: "Low-fat, high-protein, no heavy oils" },
			{ ic: "🚚", txt: "Delivered Mon · Wed · Fri across Melbourne" },
		],
		founderKicker: "Why we exist",
		founderTitle: "Built from a real problem",
		founderBody1:
			"Our co-founder is serious about training and a strict high-protein diet — at one point cooking almost a kilo of meat a day and watching it eat his life. There had to be a better way to eat clean without living at the stove.",
		founderBody2:
			"So CHDS was designed from both ends: Dish Only, so people who need volume can order clean protein at a fair price — and the Meal Set, so everyone else gets a genuinely balanced meal with zero effort.",
		whyTitle: "Why people choose CHDS",
		reasons: [
			{ ic: "☯️", title: "Balance by design", body: "Protein, vegetables and rice in proportion — the 吃荤搭素 philosophy in every Meal Set." },
			{ ic: "🍳", title: "Fresh, never frozen", body: "Chef-cooked to order in our own restaurant kitchen and delivered while it's genuinely fresh." },
			{ ic: "💪", title: "Honest macros", body: "kcal, protein, carbs and fat published — low-fat, high-protein, and it still tastes like dinner." },
			{ ic: "🍱", title: "Two formats", body: "A complete Meal Set for zero-effort balance, or Dish Only clean protein for athletes and families." },
			{ ic: "🌏", title: "Made for Melbourne", body: "Delivered Monday, Wednesday and Friday. Flat $4 delivery, free over $70, first order discounted." },
			{ ic: "💛", title: "Community roots", body: "We grew from a loyal WeChat community — warm, bilingual, personal service is who we are." },
		],
		ctaMenu: "View the menu",
		ctaBox: "Explore meal boxes",
	},
	zh: {
		heroTitle: "均衡，吃得出来",
		heroSub: "地道中式烹饪，为健康生活而设计，墨尔本新鲜直送。",
		nameKicker: "名字，就是我们的理念",
		nameTitle: "均衡，写在我们的名字里",
		nameBody1:
			"CHDS 即 Chi Hun Da Su —— 吃荤搭素。这是中国人代代相传、最朴素的营养智慧：荤素搭配、均衡饮食，每一餐都如此。我们没有发明均衡饮食，只是把它新鲜做好、装进餐盒，并把营养成分清楚地印在旁边。",
		nameBody2:
			"我们从一个小小的微信社群厨房起步，拥有一群忠实的老顾客，如今成长为完整的双语外送品牌 —— 同样的用心，现在服务整个墨尔本。",
		formatsTitle: "两种吃得健康的方式",
		formatsIntro: "一个厨房，两扇门 —— 无论你想要一餐全部搞定，还是想单点纯蛋白质、按自己的方式搭配。",
		setTag: "套餐 Meal Set",
		setTitle: "一餐，全部搞定",
		setBody:
			"营养均衡的一餐，尽在一盒：约 150g 荤菜 + 150g 蔬菜 + 150g 米饭。无需纠结、无需下厨，几分钟即可享用。还可随时加配蛋白质、米饭或蔬菜。",
		dishTag: "单点 Dish Only",
		dishTitle: "纯蛋白质，随你搭配",
		dishBody:
			"300g 或 600g 纯荤菜 —— 鸡胸、卤牛肉等，餐厅现做、干净健康。每克蛋白质更划算，专为健身与高蛋白人群打造，也非常适合在家配餐的家庭。",
		promiseTitle: "新鲜，不容妥协",
		promiseBody:
			"我们首先是一家厨房，其次才是外送。每一餐都在我们自己的餐厅厨房现点现做 —— 绝不冷冻 —— 并公开营养成分，让你清楚知道自己吃的是什么。",
		proofs: [
			{ ic: "🔥", txt: "现点现做，绝不冷冻" },
			{ ic: "📊", txt: "每道菜公开营养成分" },
			{ ic: "🥦", txt: "低脂高蛋白，不用重油" },
			{ ic: "🚚", txt: "每周一 · 三 · 五 墨尔本配送" },
		],
		founderKicker: "我们为何而做",
		founderTitle: "源自一个真实的难题",
		founderBody1:
			"我们的联合创始人热爱健身、坚持严格的高蛋白饮食 —— 曾经每天要煮将近一公斤的肉，几乎被厨房占满了生活。一定有更好的方式，既吃得干净，又不用天天守着炉子。",
		founderBody2:
			"于是 CHDS 从两端出发设计：单点 Dish Only，让需要大量蛋白质的人以公道的价格吃到干净的荤菜；套餐 Meal Set，让其他人毫不费力地享用真正均衡的一餐。",
		whyTitle: "为什么选择 CHDS",
		reasons: [
			{ ic: "☯️", title: "均衡的设计", body: "荤菜、蔬菜、米饭按比例搭配 —— 每份套餐都是「吃荤搭素」的理念。" },
			{ ic: "🍳", title: "新鲜，绝不冷冻", body: "在我们自己的餐厅厨房现点现做，趁新鲜送到你手中。" },
			{ ic: "💪", title: "诚实的营养", body: "公开热量、蛋白、碳水与脂肪 —— 低脂高蛋白，却依然像一顿正餐。" },
			{ ic: "🍱", title: "两种形式", body: "省心均衡的套餐，或为健身与家庭准备的单点纯蛋白质。" },
			{ ic: "🌏", title: "为墨尔本而做", body: "每周一、三、五配送。运费统一 $4，满 $70 免运费，首单享折扣。" },
			{ ic: "💛", title: "社群起家", body: "我们从忠实的微信社群成长而来 —— 温暖、双语、贴心的服务，就是我们。" },
		],
		ctaMenu: "查看菜单",
		ctaBox: "了解套餐箱",
	},
};

const c = computed(() => COPY[lang.value]);

const orgJsonLd = {
	"@context": "https://schema.org",
	"@type": "Organization",
	name: "CHDS — Chi Hun Da Su",
	alternateName: "吃荤搭素",
	url: "https://chds.com.au",
	logo: "https://chds.com.au/og-default.jpg",
	image: "https://chds.com.au/og-default.jpg",
	telephone: "+61489198036",
	email: "info@chds.com.au",
	address: {
		"@type": "PostalAddress",
		streetAddress: "243 Burwood Road",
		addressLocality: "Hawthorn East",
		addressRegion: "VIC",
		postalCode: "3122",
		addressCountry: "AU",
	},
	description:
		"Fresh, healthy, chef-cooked Chinese meal delivery in Melbourne. Low-fat, high-protein, never frozen, with macros published on every dish. Order a complete Meal Set or clean protein by the half-kilo (Dish Only).",
	areaServed: { "@type": "City", name: "Melbourne" },
	knowsLanguage: ["en", "zh"],
	sameAs: ["https://chds.com.au"],
};

useSEO({
	title: "Our Story — CHDS (吃荤搭素) Healthy Chinese Meal Delivery Melbourne",
	titleZh: "我们的故事 | CHDS 吃荤搭素 — 墨尔本健康中餐外送",
	descriptionZh: "CHDS 为何存在：荤素搭配的主厨中餐，在 Hawthorn East 新鲜制作，配送墨尔本全城。",
	description:
		"CHDS means 吃荤搭素 — “eat meat, paired with vegetables.” Fresh, chef-cooked, never-frozen Chinese meals with published macros, delivered across Melbourne. Complete Meal Sets or clean protein by the half-kilo.",
	keywords:
		"CHDS story, 吃荤搭素, healthy Chinese meal delivery Melbourne, high protein meal delivery, dish only protein, fresh meal delivery",
	type: "website",
	path: "/ourstory",
	jsonLd: orgJsonLd,
});
</script>

<style scoped>
.os-section { padding: 64px 0; }
.os-tint { background: #f4f7ec; }
.container-xl { max-width: 1140px; margin: 0 auto; padding: 0 18px; }

/* Hero */
.os-banner { min-height: 320px; padding: 60px 0; overflow: hidden; }
.os-banner::before {
	position: absolute; inset: 0; content: "";
	background: url("../assets/homepage/about-bg.png") center/cover no-repeat;
	z-index: -2;
}
.os-banner::after {
	position: absolute; inset: 0; content: "";
	background: linear-gradient(180deg, rgba(43, 58, 34, 0.55), rgba(43, 58, 34, 0.78));
	z-index: -1;
}
.os-banner-inner { max-width: 780px; }
.os-eyebrow { color: #cfe39a; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; font-size: 13px; margin: 0 0 12px; }
.os-banner h1 { font-size: 46px; font-weight: 800; line-height: 1.1; }
.os-hero-sub { color: #eaf1dc; font-size: 18px; margin: 16px auto 0; max-width: 620px; font-family: "Poppins", sans-serif; }

/* Headings + body */
.os-h2 { font-size: 32px; font-weight: 800; color: #2b3a22; margin: 0 0 18px; }
.os-h2--left { text-align: left; }
.os-h2--light { color: #fff; }
.os-kicker { color: #6b8a2e; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; font-size: 13px; margin: 0 0 8px; }
.os-lead { max-width: 780px; margin: 0 auto; }
.os-body { color: #4e5a44; font-size: 16.5px; line-height: 1.7; font-family: "Poppins", sans-serif; margin: 0 0 14px; }
.os-body-narrow { max-width: 720px; margin-left: auto; margin-right: auto; }

/* Two formats */
.os-formats { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 34px; }
.os-format-card {
	background: #fff; border: 1px solid #e6ecd8; border-radius: 18px; padding: 30px 28px;
	box-shadow: 0 10px 28px rgba(68, 105, 54, 0.07); transition: transform 0.25s, box-shadow 0.25s;
}
.os-format-card:hover { transform: translateY(-4px); box-shadow: 0 16px 34px rgba(68, 105, 54, 0.13); }
.os-format-card--alt { background: #2b3a22; border-color: #2b3a22; }
/* Two classes so these beat `.os-format-card h3/p` regardless of source order (dark card = light text). */
.os-format-card.os-format-card--alt h3 { color: #ffffff; }
.os-format-card.os-format-card--alt p { color: #e4edd3; }
.os-format-tag { display: inline-block; background: #eef4e0; color: #4c6b1a; font-weight: 700; font-size: 12.5px; letter-spacing: 0.4px; padding: 5px 12px; border-radius: 999px; margin-bottom: 14px; }
.os-format-card--alt .os-format-tag { background: rgba(182, 212, 82, 0.22); color: #cfe39a; }
.os-format-card h3 { font-size: 21px; font-weight: 700; color: #2b3a22; margin: 0 0 10px; }
.os-format-card p { font-size: 15.5px; line-height: 1.65; color: #566349; font-family: "Poppins", sans-serif; margin: 0; }

/* Promise band */
.os-promise { padding: 60px 0; background: linear-gradient(135deg, #446936, #2b3a22); }
.os-promise-sub { color: #e4edd3 !important; }
.os-proof-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 14px; margin-top: 30px; }
.os-proof {
	display: flex; align-items: center; gap: 10px; background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 999px; padding: 10px 18px;
}
.os-proof-ic { font-size: 18px; }
.os-proof-txt { color: #f1f6e8; font-size: 14.5px; font-weight: 600; font-family: "Poppins", sans-serif; }

/* Founder */
.os-founder { display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 40px; align-items: center; }
.os-founder-media img { border-radius: 18px; width: 100%; box-shadow: 0 14px 34px rgba(68, 105, 54, 0.16); }

/* Why grid */
.os-why-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-top: 36px; }
.os-why-card {
	background: #fff; border: 1px solid #e6ecd8; border-radius: 16px; padding: 26px 24px;
	transition: transform 0.25s, box-shadow 0.25s;
}
.os-why-card:hover { transform: translateY(-4px); box-shadow: 0 14px 30px rgba(68, 105, 54, 0.12); }
.os-why-ic { font-size: 30px; display: block; margin-bottom: 12px; }
.os-why-card h3 { font-size: 18px; font-weight: 700; color: #2b3a22; margin: 0 0 8px; }
.os-why-card p { font-size: 15px; line-height: 1.6; color: #566349; font-family: "Poppins", sans-serif; margin: 0; }

/* CTA */
.os-cta { margin-top: 44px; display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.os-btn { display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; padding: 13px 30px; font-weight: 700; font-size: 15.5px; text-decoration: none; transition: transform 0.16s, background 0.2s, box-shadow 0.2s; }
.os-btn--primary { background: #76923b; color: #fff; box-shadow: 0 8px 20px rgba(118, 146, 59, 0.28); }
.os-btn--primary:hover { background: #576d27; transform: translateY(-2px); }
.os-btn--ghost { background: #fff; color: #446936; border: 1.5px solid #cdd9b3; }
.os-btn--ghost:hover { background: #eef4e0; transform: translateY(-2px); }

@media (max-width: 900px) {
	.os-founder { grid-template-columns: 1fr; gap: 24px; }
	.os-h2--left { text-align: center; }
	.os-founder-copy .os-kicker { text-align: center; }
	.os-why-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 640px) {
	.os-section { padding: 46px 0; }
	.os-banner h1 { font-size: 34px; }
	.os-hero-sub { font-size: 16px; }
	.os-h2 { font-size: 26px; }
	.os-formats { grid-template-columns: 1fr; }
	.os-why-grid { grid-template-columns: 1fr; }
}
</style>
